'use client';

import clsx from 'clsx';
import type { FC } from 'react';
import { useState } from 'react';
import { Button } from '@/components/raven/ui/Button';
import { InternalLink } from '@/components/InternalLink';
import { ArrowUp, ArrowDown } from '@themaven-net/raven-assets';

import styles from './styles.module.css';

export interface TableOfContentsProps {
	headings: {
		anchor: string;
		label: string;
	}[];
	includeBackground?: boolean;
	includeNumbers?: boolean;
	showMoreText?: string;
	title?: string;
}

const DEFAULT_HEADINGS_LIMIT = 5;

const formatNumber = (number: number) => number.toString().padStart(2, '0');

export const TableOfContents: FC<TableOfContentsProps> = ({
	headings,
	includeBackground,
	includeNumbers,
	showMoreText = 'Show More',
	title,
}) => {
	const [showMore, setShowMore] = useState(false);

	if (!headings?.length) {
		return null;
	}

	const className = clsx(styles.tableOfContents, {
		[styles.tableOfContentsBackground]: includeBackground,
	});

	const firstFiveHeadings = headings.slice(0, DEFAULT_HEADINGS_LIMIT);
	const remainingHeadings = headings.slice(DEFAULT_HEADINGS_LIMIT);
	const hasRemainingHeadings = remainingHeadings.length > 0;

	const renderHeadingList = (
		headingList: typeof headings,
		startIndex: number = 0,
	) => (
		<ol className={styles.tableOfContentsList}>
			{headingList.map((heading, index) => (
				<li
					className={styles.tableOfContentsListItem}
					key={`${heading.label}-${formatNumber(startIndex + index + 1)}`}
				>
					<InternalLink
						className={styles.tableOfContentsLink}
						href={heading.anchor}
					>
						<span className={styles.tableOfContentsItemLabel}>
							{heading.label}
						</span>
						{includeNumbers && (
							<span className={styles.tableOfContentsItemNumber}>
								{formatNumber(startIndex + index + 1)}
							</span>
						)}
					</InternalLink>
				</li>
			))}
		</ol>
	);

	return (
		<nav aria-label="Table of Contents" className={className}>
			{title && <h2 className={styles.tableOfContentsTitle}>{title}</h2>}

			{renderHeadingList(firstFiveHeadings)}

			{hasRemainingHeadings &&
				showMore &&
				renderHeadingList(remainingHeadings, DEFAULT_HEADINGS_LIMIT)}

			{hasRemainingHeadings && (
				<Button
					className={styles.tableOfContentsToggle}
					onClick={() => setShowMore(!showMore)}
					variation="link"
				>
					{showMore ? 'Show Less' : showMoreText}
					{showMore ? <ArrowUp /> : <ArrowDown />}
				</Button>
			)}
		</nav>
	);
};
